import { UserModel } from './account-settings/models/user.model';
import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { Environment, ENVIRONMENT_DI } from './environment/environment';
import { IMtr } from './core/tracker/mtr.interface';
import { ApplicationStarterService } from './core/application-starter.service';
import { TitleService } from './core/title.service';
import { LanguageService } from './core/language.service';
import { IdentityService } from './core/identity/identity.service';
import { SiteContextService } from './core/site-context.service';
import { CurrentSiteIdStore } from './core/current-site-id.store';
import { LoaderService } from './core/loader.service';
import { filter } from 'rxjs/operators';
import { CustomersService } from './customers/customers.service';
import { Subscription, merge } from 'rxjs';
import { CurrentContextService } from './core/current-context.service';
import { DateService } from './shared/services/date-service';
import { Logger, LoggerFactory } from './core/logger.service';
import { QueryParamService } from './shared/services/query-param.service';
import { ThemeService } from './shared/services/theme.service';
import { AdminSettingsModalComponent } from './shared/admin-settings-modal/admin-settings-modal.component';
import { UserSession } from './core/user-session';
import { MatDialog } from '@angular/material/dialog';
import { ConfigureMarketAccountModalComponent } from './shared/configure-market-account-modal/configure-market-account-modal.component';
import { DomainDataConfig } from './shared/account/account.model';
import { TrackerService } from './shared/services/tracker.service';
import { SettingsService } from './shared/services/admin-settings.service';
import { UserService } from './user/user.service';
import { StaleChunkReloadService } from './core/stale-chunk-reload.service';

function serializeError(err: unknown): string {
  if (!err) return 'unknown';
  if (err instanceof Error) return `${err.message} ${err.stack ?? ''}`.trim();
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(AdminSettingsModalComponent) adminModal!: AdminSettingsModalComponent;

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    this.isAdmin = this.userSession.context.isAdmin;
    if (!this.isAdmin) {
      return;
    }
    const isCtrlPressed = event.ctrlKey;
    const isAltPressed = event.altKey;
    const isSpacePressed = event.code === 'Space'; // Use 'code' for consistency across layouts

    if (isCtrlPressed && isAltPressed && isSpacePressed) {
      event.preventDefault();
      this.adminModal.open();
    }
  }

  private readonly staleChunkReloadService = inject(StaleChunkReloadService);
  private readonly logger: Logger;
  public currentTitle: string;
  public environment: Environment;
  public isBetaItem = false;
  public layoutColor: string;
  public isFullscreenPage = true;
  public currentCustomerSubscription: Subscription;
  public identitySubscription: Subscription;
  private subscriptionArray: Subscription[] = [];
  private currentContextSubscription: Subscription;
  private openAdminSettingsModalSubscription: Subscription;
  private queryParamsSubscription: Subscription;
  private routerEventsSubscription: Subscription;
  private routerNavigationEndSubscription: Subscription;
  private userInitSubscription: Subscription;
  private fullscreenPageUrls = [
    '/account/login',
    '/account/register',
    '/account/domain',
    '/account/company-type',
    '/account/integrations',
    '/account/install',
    '/oauth-consent',
    '/invitations/',
    '/campaigns/ab-tests/create/*',
    '/campaigns/create/*',
    '/campaigns/*/widgets/*/editor',
    '/campaigns/static-widgets/*/editor',
    '/campaigns/*/widgets/*/settings/wizard',
    '/campaigns/*/settings',
    '/user/resetpassword',
    '/user/reset-mfa',
    '/user/activate',
    '/audiences/create',
    '/audiences/*/settings',
    '/settings/v2*',
  ];
  public isAdmin = false;

  constructor(
    loggerFactory: LoggerFactory,
    private starter: ApplicationStarterService,
    private identityService: IdentityService,
    private titleService: TitleService,
    private readonly siteContext: SiteContextService,
    private readonly currentSiteIdStore: CurrentSiteIdStore,
    private readonly loaderService: LoaderService,
    private readonly customersService: CustomersService,
    private readonly currentContextService: CurrentContextService,
    @Inject(ENVIRONMENT_DI) environment: Environment,
    @Inject('Mtr') private readonly mtr: IMtr,
    public router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dateService: DateService,
    private readonly queryParamService: QueryParamService,
    private readonly themeService: ThemeService,
    protected readonly userSession: UserSession,
    private dialog: MatDialog,
    private settingsService: SettingsService,
    private userService: UserService,
    // Eager instantiation: wires identity/settings subscriptions before they fire,
    // otherwise ngx-translate gets no language and the UI renders raw keys.
    private readonly languageService: LanguageService,
  ) {
    this.logger = loggerFactory.get('AppComponent');
    this.environment = environment;
    this.initRouteChangeMonitoring();
    this.staleChunkReloadService.initialize();
  }

  public ngOnInit() {
    this.starter
      .initializeAsync()
      .then(() => {
        this.addGoogleTagManager(this.environment.gtmId);
        this.addTrackingScript();
        if (this.identityService.hasCurrentIdentity) {
          this.userInitSubscription = this.userService.getUserAsObservable().subscribe({
            next: (user: UserModel) => {
              this.themeService.init(user);
              this.queryParamService.init(user);
              this.currentContextService.loadCurrentContext(false, user);
            },
            error: (err) => this.logger.error(`Failed to load user during app init: ${serializeError(err)}`),
          });
          this.refreshJwt();
          this.customersService.loadCurrentCustomer();
          this.currentCustomerSubscription = this.customersService.customerInitializedBS.subscribe(() => {});
        }
      })
      .catch((err) => {
        this.logger.error(`ApplicationStarterService.initializeAsync failed: ${serializeError(err)}`);
      });
    this.currentContextSubscription = this.currentContextService.currentContextBS.subscribe((currentContext) => {
      if (currentContext) {
        this.configureMarketAccount(currentContext.account);
      }
    });

    setTimeout(() => {
      this.isAdmin = this.userSession.context.isAdmin;
      this.openAdminSettingsModalSubscription = this.settingsService.openAdminSettingsModal.subscribe(() => {
        if (this.adminModal) this.adminModal.open();
      });
    }, 0);
  }

  private refreshJwt() {
    if ((this.currentSiteIdStore.get() ?? 0) > 0) {
      // When JWT is not refreshed for one day we do it now
      if (this.dateService.isOlderThanOneDay(this.identityService.currentIdentity.issuedAt)) {
        this.identityService
          .requestUpdateIdentityPayloadAsync()
          .then((result: boolean) => {
            if (result) {
              this.identityService.notifyPossibleIdentityUpdate();
            }
          })
          .catch((err) => {
            // Swallow transient failures here; IdentityService already logs + signs out on hard failures.
            this.logger.warn(`JWT refresh rejected during app init: ${serializeError(err)}`);
          });
      }

      // On account invite or refreshjwt query param we refresh JWT.
      this.queryParamsSubscription = this.activatedRoute.queryParams.subscribe((queryParams) => {
        if (queryParams['utm_campaign'] === 'account-invite' || queryParams['refreshjwt']) {
          this.identityService
            .requestUpdateIdentityPayloadAsync()
            .then(() => {
              this.identityService.notifyPossibleIdentityUpdate();
            })
            .catch((err) => {
              this.logger.warn(`JWT refresh rejected for refreshjwt query param: ${serializeError(err)}`);
            });
        }
      });
    }
  }

  public ngOnDestroy() {
    this.subscriptionArray.push(
      this.currentCustomerSubscription,
      this.identitySubscription,
      this.currentContextSubscription,
      this.openAdminSettingsModalSubscription,
      this.queryParamsSubscription,
      this.routerEventsSubscription,
      this.routerNavigationEndSubscription,
      this.userInitSubscription,
    );
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  private checkIfFullScreenPage(url) {
    let fullscreenPageFound = false;
    for (let i = 0; i < this.fullscreenPageUrls.length; i++) {
      if (!fullscreenPageFound) {
        const partsToCheck = this.fullscreenPageUrls[i].split('*');
        let partsMatched = 0;
        for (let y = 0; y < partsToCheck.length; y++) {
          if (url.indexOf(partsToCheck[y]) !== -1) {
            partsMatched++;
          }
        }
        if (partsMatched === partsToCheck.length) {
          fullscreenPageFound = true;
        }
      }
    }
    this.isFullscreenPage = fullscreenPageFound;
  }

  async ngAfterViewInit() {
    this.starter.notifyViewIsInitialized();
  }

  private addGoogleTagManager(gtmId: string) {
    if (gtmId) {
      const scriptId = 'gtm-script-' + gtmId;
      const existingGtmElementById = document.getElementById(scriptId);
      if (existingGtmElementById) {
        this.logger.warn('GTM not added - Already exists in DOM');
      } else {
        const script = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId}');`;

        this.executeScript(script, scriptId);
      }
    } else {
      this.logger.warn('GTM not added - ID not found');
    }
  }

  private executeScript(script: string, htmlId?: string) {
    const node = document.createElement('script');
    if (htmlId) {
      node.id = htmlId;
    }
    node.innerHTML = script;
    node.type = 'text/javascript';
    document.getElementsByTagName('head')[0].appendChild(node);
  }

  private addTrackingScript() {
    // Done through GTM for all Azure environments, therefore we only do this locally
    if (this.environment.name === 'local') {
      const siteId = 250807; // "dev.triggerbee.com"-account - Move value to enviroment IF we will include script here from other environments as well
      const trackingScript = TrackerService.getTrackingScript(siteId, `http://${this.environment.trackerDomain}`); // Used used locally, therefore http
      this.executeScript(trackingScript);
    }
  }

  private initRouteChangeMonitoring(): void {
    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.loaderService.setBusy();
          break;
        }

        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.loaderService.removeBusy();
          break;
        }
        default: {
          break;
        }
      }
    });

    // A language switch re-translates the tab title too; otherwise it keeps the old language until the next page.
    this.routerNavigationEndSubscription = merge(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
      this.languageService.languageChanged,
    ).subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        await this.handleRouteChange();
      } else if (this.router.navigated) {
        await this.updateTitle();
      }
    });
  }

  private async updateTitle() {
    const snapshot = this.router.routerState.snapshot.root;
    await this.titleService.triggerbeeGenerateTitle(snapshot);
    this.currentTitle = await this.titleService.getHighestTitle(snapshot);
  }

  private async handleRouteChange() {
    this.checkIfFullScreenPage(this.router.url);

    await this.updateTitle();
    // Although it is not looking so good I kept this line for future use. It should be refactored to use data from routing
    this.isBetaItem = false; // this.router.url.indexOf('/response-lists') === 0;

    // Inform tracker
    const currentSite = await this.siteContext.getCurrentSiteAsync();
    this.mtr.setIdentity(this.identityService.currentIdentity, currentSite); // TODO: Only when user signing in instead of here
    this.mtr.log(window.location.pathname, this.currentTitle, 'pageview');
  }

  public configureMarketAccount(account): void {
    const indexMarket = account.name.indexOf(DomainDataConfig.MarketAccount);
    if (indexMarket === -1) return;
    this.dialog.open(ConfigureMarketAccountModalComponent, {
      width: '500px',
      data: { account },
    });
  }
}
